import { useEffect, useState } from 'react';
import { SuperAdminModal } from '../../../baseComponents';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  addPractitionerAction,
  getFronteggRoles,
  getPractitionersOrgLevel,
  organizationRoles,
  organizationSpecialties,
} from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import * as Yup from 'yup';
import {
  getAutoGeneratePractitionerDetailsSlice,
  getPractitionerByIdSlice,
} from 'src/modules/SuperAdmin/store/slices/practitioners.slice';
import { DownArrowIcon } from 'src/icons/Downarrow.icon';
import { getAllConditionListAction } from 'src/modules/SuperAdmin/store/actions';
import { currentPractitioner } from 'src/store';

const AddPractitionerPage = ({
  isOpen,
  setIsOpen,
  setSelectedPractitioner,
  persistedOrgUUID,
}) => {
  const [initialOrgId, setInitialOrgId] = useState('');
  const initialValues = {
    org_id: initialOrgId,
    practitioner_id: '',
    condition_id: '',
    mirror_practitioner_id: null,
    get_role: '',
    get_primary_specialty: '',
    first_name: '',
    last_name: '',
    is_shown_flag: false,
    address_1: '',
    address_2: '',
    zip_code: '',
    city: '',
    state: '',
    country: '',
    phone_text: '',
    email_text: '',
    frontegg_roles: [],
    ehr_id: '',
  };
  const validationSchemaField = Yup.object().shape({
    condition_id: Yup.array().of(Yup.string()).min(1, 'Condition is required'),
    org_id: Yup.string().required('Organization is required'),
    zip_code:
      initialValues?.zip_code !== '' &&
      Yup.string()
        .min(5, 'Max 5 digits for USA zip code')
        .max(5, 'Max 5 digits for USA zip code'),
    first_name: Yup.string()
      .required('First name required')
      .matches(/[A-Za-z]+$/, ' ')
      .matches(/^[^\s].*$/, ' '),
    last_name: Yup.string()
      .required('Last name is required')
      .matches(/[A-Za-z]+$/, ' ')
      .matches(/^[^\s].*$/, ' '),
    email_text: Yup.string()
      .required('Email is required')
      .matches(/^.+@.+\..+$/, 'Invalid email format')
      .matches(/^[^\s].*$/, 'Invalid email format'),
    get_role: Yup.string().required('Role is required'),
    get_primary_specialty: Yup.string().required(
      'Primary specialty is required'
    ),
    frontegg_roles: Yup.array()
      .of(Yup.string())
      .min(1, 'Frontegg Role is required'),
    ehr_id: Yup.string(),
  });

  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [tableLoader, setTableLoader] = useState(false);
  const { getAllConditionsList, isLoading } = useSelector(
    (state) => state?.conditionState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const {
    practitionersOrgLevel,
    getPractitionerById,
    autoGeneratePractitionerDetails,
    fronteggRoles,
  } = useSelector((state) => state?.doctorsState);

  const [practitionerOptions, setPractitionerOptions] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [fronteggRoleOptions, setFronteggRoleOptions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleID, setRoleID] = useState(null);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [specialtyID, setSpecialtyID] = useState(null);
  const [storeConditionId, setStoreConditionId] = useState([]);
  const [isOrganizationSelected, setIsOrganizationSelected] =
    useState(!!persistedOrgUUID);
  const [storeOrganizationUuid, setStoreOrganizationUuid] = useState('');
  const [isExpandedAddress, setIsExpandedAddress] = useState(false);
  const [storeMirrorPractitionerId, setStoreMirrorPractitionerId] =
    useState(null);
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    dispatch(getAllConditionListAction());
    dispatch(organizationRoles());
    dispatch(organizationSpecialties());
    dispatch(getFronteggRoles(currentPractitioner?.org_uuid));
  }, [dispatch]);

  useEffect(() => {
    if (persistedOrgUUID && organizationOptions.length > 0) {
      const matchingOrg = organizationOptions.find(
        (org) => org.org_uuid === persistedOrgUUID
      );
      if (matchingOrg) {
        setInitialOrgId(matchingOrg.value);
        setStoreOrganizationUuid(matchingOrg.org_uuid);
        setIsOrganizationSelected(true);
        dispatch(getPractitionersOrgLevel(matchingOrg.org_uuid));
      }
    }
  }, [isOpen, persistedOrgUUID, organizationOptions, dispatch]);

  useEffect(() => {
    if (autoGeneratePractitionerDetails?.condition_details?.length > 0) {
      const conditionIds = [
        ...new Set(
          autoGeneratePractitionerDetails?.condition_details?.map(
            (condition) => condition.condition_id
          )
        ),
      ];
      setStoreConditionId(conditionIds);
    }
    setRoleID(autoGeneratePractitionerDetails?.role_details?.role_id);
    setSpecialtyID(
      autoGeneratePractitionerDetails?.specialty_details?.specialty_id
    );
  }, [autoGeneratePractitionerDetails]);

  const { getOnboardingSpecialties, getOnboardingRoles } = useSelector(
    (state) => state?.onboardingState
  );

  useEffect(() => {
    if (
      getOnboardingSpecialties &&
      Array.isArray(getOnboardingSpecialties) &&
      getOnboardingSpecialties.length > 0
    ) {
      const dataArr = getOnboardingSpecialties.map((specialty, index) => ({
        key: `${specialty.specialty_id}_${index}`,
        value: specialty.specialty_id,
        label: specialty.specialty_name ? specialty.specialty_name : 'N/A',
      }));
      setSpecialtyOptions(dataArr);
    }
  }, [getOnboardingSpecialties]);

  useEffect(() => {
    if (
      getOnboardingRoles &&
      Array.isArray(getOnboardingRoles) &&
      getOnboardingRoles.length > 0
    ) {
      const dataArr = getOnboardingRoles.map((roles, index) => ({
        key: `${roles.role_id}_${index}`,
        value: roles.role_id,
        label: roles.role_name ? roles.role_name : 'N/A',
      }));
      setRoleOptions(dataArr);
    }
  }, [getOnboardingRoles]);

  useEffect(() => {
    setTableLoader(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (practitionersOrgLevel) {
      const dataArr = practitionersOrgLevel.map((list) => ({
        value: list.practitioner_id,
        label: list.practitioner_name,
      }));

      const tableArr = practitionersOrgLevel.map((list) => ({
        practitioner_id: list.practitioner_id,
        conditions: list.conditions.map((items) => ({
          condition_id: items.condition_id,
          condition_name: items.condition_name,
          notification_preference: items.notification_preferences.flat() || [],
        })),
      }));

      setPractitionerOptions(dataArr);
      setTableData(tableArr);
    }
  }, [practitionersOrgLevel]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationOptions(dataArr);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (fronteggRoles && Array.isArray(fronteggRoles)) {
      let dataArr = [];
      fronteggRoles.forEach((key) => {
        dataArr.push({
          value: key?.name,
          label: key?.name,
        });
      });
      setFronteggRoleOptions(dataArr);
    }
  }, [fronteggRoles]);

  useEffect(() => {
    if (
      getAllConditionsList &&
      Array.isArray(getAllConditionsList) &&
      getAllConditionsList.length > 0
    ) {
      const dataArr = getAllConditionsList.map((condition, index) => ({
        key: `${condition.condition_id}_${index}`,
        value: condition.condition_id,
        label: `${condition.specialty_name} - ${condition.condition_name ? condition.condition_name : 'N/A'}`,
        specialty_name: condition.specialty_name,
      }));
      setConditionOptions(dataArr);
    } else {
      setConditionOptions([]);
    }
  }, [getAllConditionsList, getPractitionerById]);

  const columns = [
    {
      title: <span className="font-[700]"> Condition name</span>,
      dataIndex: 'condition_name',
      key: 'condition_name',
      width: '10%',
    },
    {
      title: <span className="font-[700]"> Schedule</span>,
      dataIndex: 'schedule',
      key: 'schedule',
      width: '20%',
    },
  ];

  const getTableDataByPractitionerId = (practitionerId) => {
    const practitionerData = tableData.find(
      (item) => item.practitioner_id === practitionerId
    );
    if (!practitionerData) return [];

    return practitionerData.conditions.map((condition) => ({
      condition_name: condition.condition_name,
      schedule: condition.notification_preference
        .map((pref) => `${pref.number_of} ${pref.duration}`)
        .join(', '),
    }));
  };

  useEffect(() => {
    if (isOpen && storeOrganizationUuid) {
      dispatch(getPractitionersOrgLevel(storeOrganizationUuid));
    }
  }, [isOpen, storeOrganizationUuid, dispatch]);

  const sections = [
    {
      heading: 'Add user',
      fields: [],
    },
    {
      className: 'grid grid-cols-3 gap-x-4 gap-y-6',
      fields: [
        {
          name: 'org_id',
          type: 'select',
          title: 'Organization',
          placeholder: 'Select organization',
          options: organizationOptions,
          onChange: (setFieldValue, e, value) => {
            setFieldValue('org_id', e);
            dispatch(getPractitionersOrgLevel(value?.org_uuid));
            setStoreOrganizationUuid(value?.org_uuid);
            setIsOrganizationSelected(true);
            setFieldValue('practitioner_id', '');
            setFieldValue('condition_id', '');
            setFieldValue('get_primary_specialty', null);
            setFieldValue('get_role', null);
            setFieldValue('ehr_id', '');
            setStoreMirrorPractitionerId(null);
          },
        },
        {
          name: 'other_users',
          type: 'add_mirror_user',
          title: 'Mirror user',
        },
        {
          name: 'get_role',
          type: 'select',
          title: 'Role',
          placeholder: 'Select role',
          inputClassName: 'overflow-auto flex items-center grid grid-cols-0',
          options: roleOptions,
          onChange: (setFieldValue, e) => {
            setFieldValue('get_role', e);
            setRoleID(e);
          },
        },
        {
          name: 'frontegg_roles',
          type: 'select',
          title: 'Frontegg role',
          placeholder: 'Select frontegg role',
          dropdownMode: 'multiple',
          options: fronteggRoleOptions,
          onChange: (setFieldValue, e) => {
            setFieldValue('frontegg_roles', e);
          },
        },
        {
          name: 'get_primary_specialty',
          type: 'select',
          title: 'Primary specialty',
          placeholder: 'Select primary specialty',
          inputClassName: 'overflow-auto flex items-center grid grid-cols-0',
          options: specialtyOptions,
          disabled: isOrganizationSelected ? false : true,
          onChange: (setFieldValue, e) => {
            setFieldValue('get_primary_specialty', e);
            setSpecialtyID(e);
          },
        },
        {
          name: 'condition_id',
          type: 'select',
          title: 'Assign conditions',
          placeholder: 'Select condition',
          dropdownMode: 'multiple',
          options: conditionOptions,
          disabled: isOrganizationSelected ? false : true,
          onChange: (setFieldValue, e) => {
            setFieldValue('condition_id', e);
            setStoreConditionId(e);
          },
        },
        {
          name: 'first_name',
          type: 'text',
          title: 'First name',
          placeholder: 'Enter first name',
          autoComplete: 'off',
        },
        {
          name: 'last_name',
          type: 'text',
          title: 'Last name',
          placeholder: 'Enter last name',
          autoComplete: 'off',
        },
        {
          name: 'email_text',
          type: 'text',
          title: 'Email',
          placeholder: 'Enter email',
          autoComplete: 'off',
        },
        {
          name: 'phone_text',
          type: 'phonenumber_text',
          title: 'Phone number',
          placeholder: 'Enter phone number',
          autoComplete: 'off',
        },
        {
          name: 'ehr_id',
          type: 'ehr_id_text',
          title: 'EHR ID',
          placeholder: 'Enter EHR ID',
          autoComplete: 'off',
        },
      ],
    },
    {
      fields: [
        {
          helperTitle: <span>Show in scheduling</span>,
          name: 'is_shown_flag',
          type: 'switch',
          onChange: (value, option, setFieldValue) => {
            setIsShown(value);
            setFieldValue('is_shown_flag', value);
          },
        },
      ],
    },
    {
      heading: (
        <>
          <div className="flex items-center">
            <div>Add address</div>
            <div className="mx-2 mt-1">
              <DownArrowIcon
                className={`arrow-icon-rotate ${isExpandedAddress ? 'open' : ''}`}
                onClick={() => setIsExpandedAddress(!isExpandedAddress)}
              />
            </div>
          </div>
        </>
      ),
      fields: [],
    },
    {
      className: 'grid grid-cols-3 gap-4',
      fields: isExpandedAddress
        ? [
            {
              name: 'address_1',
              type: 'text',
              title: 'Address 1',
              placeholder: 'Enter address 1',
              autoComplete: 'off',
            },
            {
              name: 'address_2',
              type: 'text',
              title: 'Address 2',
              placeholder: 'Enter address 2',
              autoComplete: 'off',
            },
            {
              name: 'city',
              type: 'text',
              title: 'City',
              placeholder: 'Enter city name',
              autoComplete: 'off',
            },
            {
              name: 'state',
              type: 'text',
              title: 'State',
              placeholder: 'Enter state name',
              autoComplete: 'off',
            },
            {
              name: 'country',
              type: 'text',
              title: 'Country',
              placeholder: 'Enter country name',
              autoComplete: 'off',
            },

            {
              name: 'zip_code',
              type: 'text',
              title: 'Zipcode',
              placeholder: 'Enter zipcode',
              autoComplete: 'off',
            },
          ]
        : [],
    },
  ];

  return (
    <SuperAdminModal
      open={isOpen}
      sections={sections}
      validationSchema={validationSchemaField}
      className={`prompt-modal`}
      initialValues={initialValues}
      closable={true}
      submitButtonText="Submit"
      cancelButtonText="Cancel"
      centered={true}
      isShown={isShown}
      isOrganizationSelected={isOrganizationSelected}
      setStoreMirrorPractitionerId={setStoreMirrorPractitionerId}
      storeOrganizationUuid={storeOrganizationUuid}
      storeMirrorPractitionerId={storeMirrorPractitionerId}
      practitionerOptions={practitionerOptions}
      columns={columns}
      getTableDataByPractitionerId={getTableDataByPractitionerId}
      tableLoader={tableLoader}
      storeConditionId={storeConditionId}
      setStoreConditionId={setStoreConditionId}
      conditionOptions={conditionOptions}
      fronteggRoleOptions={fronteggRoles}
      handleCancel={() => {
        setPractitionerOptions([]);
        setIsOpen(false);
        // setIsOrganizationSelected(false);
        dispatch(getPractitionerByIdSlice(null));
        setStoreMirrorPractitionerId(null);
        setStoreConditionId([]);
        setIsShown(false);
        setIsExpandedAddress(false);
        setPractitionerOptions([]);
        dispatch(getAutoGeneratePractitionerDetailsSlice(null));
      }}
      handleSubmit={(values) => {
        const payload = {
          org_uuid: getPractitionerById?.Organization_details?.org_uuid
            ? getPractitionerById?.Organization_details?.org_uuid
            : storeOrganizationUuid,
          conditions: storeConditionId,
          mirror_practitioner_id: storeMirrorPractitionerId
            ? storeMirrorPractitionerId
            : null,
          role_id: roleID,
          primary_specialty_id: specialtyID,
          first_name: values?.first_name,
          last_name: values?.last_name,
          is_shown: isShown,
          create_frontegg_user: true,
          frontegg_roles: values?.frontegg_roles,
          ehr_id: values?.ehr_id,
          email: {
            email_text: values.email_text,
            email_type: '',
          },
          phone: {
            phone_text: values.phone_text,
            phone_type: '',
          },
          address: {
            address_type: '',
            line_1: values?.address_1,
            line_2: values?.address_2,
            city: values?.city,
            state: values?.state,
            country: values?.country,
            zip: values?.zip_code,
          },
        };
        dispatch(
          addPractitionerAction(
            payload,
            setIsOpen,
            setIsOrganizationSelected,
            persistedOrgUUID,
            storeOrganizationUuid,
            setPractitionerOptions,
            setStoreMirrorPractitionerId,
            setIsShown,
            setSelectedPractitioner
          )
        );
      }}
    />
  );
};

export default AddPractitionerPage;
